import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, input, output, signal, viewChild } from '@angular/core';

import { AssetCard } from '@features/assets/components/asset-card/asset-card';
import {
    DisplayFormat,
    IAssetListItem,
    ListSize,
} from '@features/assets/components/asset-card/asset-list-item.types';
import { CatalogueRowSkeleton } from '@features/assets/components/catalogue-row-skeleton/catalogue-row-skeleton';

@Component({
    selector: 'asset-list',
    standalone: true,
    imports: [AssetCard, CatalogueRowSkeleton],
    templateUrl: './asset-list.html',
    styleUrl: './asset-list.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetList {
    readonly title = input<string | null>(null);
    readonly items = input.required<readonly IAssetListItem[]>();
    readonly displayFormat = input<DisplayFormat>('landscape');
    readonly size = input<ListSize>('small');
    readonly selectedId = input<string | null>(null);
    readonly topChart = input(false);
    /** Passed through to every card; see `AssetCard.showTime` for the default. */
    readonly showTime = input(true);
    readonly isUpcomingCatalogue = input(false);
    readonly removeItemConfig = input<{ itemRemovable?: boolean; itemType?: string } | null>(null);
    /** referenceId of the item currently playing — swaps its card's play button for stop (radio). */
    readonly activeId = input<string | null>(null);
    /** When set, cover the row with a skeleton while it auto-scrolls the selected item into
     *  view, so the snap-into-view (and any re-scroll after picking another item) isn't visible. */
    readonly skeletonOnScroll = input(false);
    /** data-testid stamped on every card in this list (QA e2e hook). */
    readonly cardTestId = input<string>('rail_card');

    readonly titleClick = output<void>();
    readonly itemClick = output<IAssetListItem>();
    /** Radio circle cards have their own play/stop buttons; forwarded from asset-card. */
    readonly play = output<IAssetListItem>();
    readonly stop = output<IAssetListItem>();
    /**
     * Emitted once when the horizontal scroll nears the right end, so a paged caller can
     * fetch the next page. Re-arms only after the track grows (new items) and is scrolled
     * back past the threshold, so it won't spam while parked at the end.
     */
    readonly reachedEnd = output<void>();

    private readonly track = viewChild<ElementRef<HTMLDivElement>>('track');

    /** Last id we auto-scrolled to — avoids re-scrolling on unrelated re-renders. */
    private lastScrolledId: string | null = null;

    /** True while auto-scrolling to the selected item; drives the skeleton overlay. */
    readonly isSeeking = signal(false);

    /** Tears down the in-flight scroll-end watcher (listener + timeout) when a new scroll starts. */
    private endSeekWatch: (() => void) | null = null;

    readonly hasItems = computed(() => this.items().length > 0);

    constructor() {
        // Scroll the selected item into view (right-aligned, matching the legacy
        // EPG row behavior where "current" sits at the right edge with past events to its left).
        effect(() => {
            const id = this.selectedId();
            const items = this.items();
            if (!id || items.length === 0) return;
            if (id === this.lastScrolledId) return;

            const idx = items.findIndex(i => i.referenceId === id);
            if (idx < 0) return;

            if (this.skeletonOnScroll()) this.isSeeking.set(true);

            // Wait until the children are in the DOM after this change-detection cycle.
            requestAnimationFrame(() => {
                const track = this.track()?.nativeElement;
                const target = track?.children[idx] as HTMLElement | undefined;
                if (!track || !target) {
                    this.stopSeeking();
                    return;
                }

                const scrollLeft = Math.max(0, target.offsetLeft + target.offsetWidth - track.clientWidth);
                this.lastScrolledId = id;

                if (!this.skeletonOnScroll()) {
                    track.scrollTo({ left: scrollLeft, behavior: 'smooth' });
                    return;
                }

                // Already in place — nothing will scroll, so reveal on the next frame.
                if (Math.abs(track.scrollLeft - scrollLeft) < 1) {
                    this.stopSeeking();
                    return;
                }

                this.watchScrollEnd(track);
                track.scrollTo({ left: scrollLeft, behavior: 'smooth' });
            });
        });
    }

    /** Reveal the row once the smooth scroll settles — on `scrollend` where supported, else a timeout. */
    private watchScrollEnd(track: HTMLDivElement): void {
        this.endSeekWatch?.();

        const finish = () => this.stopSeeking();
        track.addEventListener('scrollend', finish);
        const timer = setTimeout(finish, 1200);

        this.endSeekWatch = () => {
            clearTimeout(timer);
            track.removeEventListener('scrollend', finish);
            this.endSeekWatch = null;
        };
    }

    private stopSeeking(): void {
        this.endSeekWatch?.();
        this.isSeeking.set(false);
    }

    onTitleClick(): void {
        this.titleClick.emit();
    }

    onItemClick(item: IAssetListItem): void {
        this.itemClick.emit(item);
    }

    scroll(direction: 'previous' | 'next'): void {
        const el = this.track()?.nativeElement;
        if (!el) return;
        const delta = el.clientWidth * 0.9 * (direction === 'next' ? 1 : -1);
        el.scrollBy({ left: delta, behavior: 'smooth' });
    }

    /** Guards reachedEnd against repeated emits while parked at the right edge. */
    private endEmitted = false;
    /** Prefetch the next page roughly one viewport-width before the true end. */
    private static readonly END_THRESHOLD_PX = 600;

    onTrackScroll(): void {
        const el = this.track()?.nativeElement;
        if (!el) return;
        const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft;
        if (remaining <= AssetList.END_THRESHOLD_PX) {
            if (!this.endEmitted) {
                this.endEmitted = true;
                this.reachedEnd.emit();
            }
        } else {
            this.endEmitted = false;
        }
    }

    trackByRef = (_: number, item: IAssetListItem): string => item.referenceId ?? '';
}
